import {
  AdvisorPermission,
  HouseholdInfoPath,
  RETIRED_VIEW_ALL_DATA,
  VIEW_ALL_DATA_GRANTS,
} from "./types";

export const prefixFields = <T extends string>(
  fields: readonly T[],
  prefix: "client" | "coClient"
): HouseholdInfoPath[] => {
  return fields.map((field) => `${prefix}.${field}` as HouseholdInfoPath);
};

// Restricted advisors are non-super-users whose division doesn't include
// Wealth Management, Retirement Plan Services, or N/A. They're limited to
// their assigned households and have a reduced feature set (e.g. no document
// uploads).
export const isRestrictedAdvisor = (
  advisor?: Forme.Advisor | null
): boolean => {
  if (!advisor) return true;
  if (advisor.isSuperUser) return false;
  return !(advisor.division ?? "")
    .split(";")
    .map((d) => d.trim())
    .some(
      (d) =>
        d === "Wealth Management" ||
        d === "Retirement Plan Services" ||
        d === "N/A"
    );
};

// Household visibility permissions, granted per advisor by an admin (see
// docs/advisor-permissions.md). None of these read isSuperUser: super user is
// a separate permission, and callers check it first.
// string[] rather than AdvisorPermission[] so a Prisma row can be passed as is.
type AdvisorWithPermissions = { permissions?: readonly string[] | null };

// A stored VIEW_ALL_DATA reads as the pair it meant (see VIEW_ALL_DATA_GRANTS).
const hasPermission = (
  advisor: AdvisorWithPermissions | null | undefined,
  permission: AdvisorPermission
): boolean =>
  !!advisor?.permissions?.some(
    (held) =>
      held === permission ||
      (held === RETIRED_VIEW_ALL_DATA &&
        VIEW_ALL_DATA_GRANTS.includes(permission))
  );

export const canViewAllNonEmployeeHouseholds = (
  advisor?: AdvisorWithPermissions | null
): boolean => hasPermission(advisor, "VIEW_ALL_NON_EMPLOYEE_HOUSEHOLDS");

export const canViewAllEmployeeHouseholds = (
  advisor?: AdvisorWithPermissions | null
): boolean => hasPermission(advisor, "VIEW_ALL_EMPLOYEE_HOUSEHOLDS");

// Which households an advisor may see. The ONE statement of the policy: the
// household list (householdVisibilityWhere), single-household reads
// (advisorCanAccessHousehold) and the Households page's filter pinning
// (isLimitedToAssignedHouseholds) all derive from it. Pure and Prisma-free so
// the browser can use it too.
//
//   • super users → every household
//   • everyone → households they are assigned to (on the account team)
//   • plus non-employee households, firm-wide, for an unrestricted advisor
//     (division Wealth Mgmt / RPS / N/A) or one granted
//     VIEW_ALL_NON_EMPLOYEE_HOUSEHOLDS
//   • plus employee households, firm-wide, for one granted
//     VIEW_ALL_EMPLOYEE_HOUSEHOLDS
//
// Assignment always counts, so the employee permission is a backup for being
// on the account team, never a requirement.
export type HouseholdVisibility =
  | { all: true }
  | { all: false; nonEmployee: boolean; employee: boolean };

export const getHouseholdVisibility = (
  advisor: Forme.Advisor
): HouseholdVisibility => {
  if (advisor.isSuperUser) return { all: true };
  return {
    all: false,
    nonEmployee:
      !isRestrictedAdvisor(advisor) || canViewAllNonEmployeeHouseholds(advisor),
    employee: canViewAllEmployeeHouseholds(advisor),
  };
};

// True when the policy leaves an advisor nothing beyond their assigned
// households. The Households page uses this to pin the My Households filter.
export const isLimitedToAssignedHouseholds = (
  advisor?: Forme.Advisor | null
): boolean => {
  if (!advisor) return true;
  const visibility = getHouseholdVisibility(advisor);
  return !visibility.all && !visibility.nonEmployee && !visibility.employee;
};
